import { getThemeCssVars } from "$root/utils";
import { minutes, seconds } from "$root/utils/time";
import { formatNumber } from "$root/localization/formatters";
import { compareAtPrecision, exactSum, roundAwayFrom } from "$root/utils/monetary-math";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { InvestmentStatus, SaveEditorEditEntityEntityEnum } from "@eu.mdotm.api/sphere";
import type { LabComputeEvent, LabComputeStatusEnum } from "@eu.mdotm.api/portfolio-lab";
import { unreachable } from "@mdotm/utils/runtime";
import type {
	BotCompute,
	PendingCompute,
	PortfolioLabComposition,
	PortfolioLabInvestmentItem,
	PortfolioLabInvestmentType,
} from "./ctx";

export const DEFAULT_LAB_AUM = 0;

// #region Column colors

const COLUMN_COLORS = [
	getThemeCssVars(themeCSSVars.palette_S500),
	getThemeCssVars(themeCSSVars.palette_A500),
	getThemeCssVars(themeCSSVars.palette_V500),
	getThemeCssVars(themeCSSVars.palette_R500),
	getThemeCssVars(themeCSSVars.palette_B500),
] as const;

export function generateColumnColor(index: number): string {
	return COLUMN_COLORS[index % COLUMN_COLORS.length];
}

// #endregion

// #region Status utilities

export function isEditableInvestment(item: PortfolioLabInvestmentItem): boolean {
	return item.type === "INVESTMENT" || item.status === "PROPOSAL_READY";
}

/**
 * Statuses in which a real investment is "consistent" — settled enough that its composition can be
 * safely overwritten by an "Update composition" commit. Transient/in-progress states
 * (`CALCULATING`, `RETRIEVING_DATA`, `EXECUTING`), drafts, reviews and errors are excluded.
 * A `PROPOSAL_READY` proposal is committed with `editPolicy: "SAVE_WITHOUT_ACCEPT"`
 * (see `repository.updateRealInvestmentComposition`).
 */
const UPDATABLE_INVESTMENT_STATUSES: ReadonlySet<InvestmentStatus> = new Set<InvestmentStatus>([
	"READY",
	"ACCEPTED",
	"PROPOSAL_READY",
]);

/** Whether an investment's status allows committing a new composition via "Update composition". */
export function isUpdatableInvestmentStatus(status: InvestmentStatus): boolean {
	return UPDATABLE_INVESTMENT_STATUSES.has(status);
}

/**
 * Whether the column's charts show reports that do not describe the composition on screen: a write
 * is still on its way to the server, the last one failed (the screen holds edits the server does not
 * have), or the stored reports were computed from an earlier revision than the server's.
 */
export function chartsStale(
	item: Pick<
		PortfolioLabInvestmentItem,
		"pendingWrites" | "compositionSaveStatus" | "compositionRevision" | "reportsComputedAtRevision"
	>,
): boolean {
	return (
		item.pendingWrites > 0 ||
		item.compositionSaveStatus === "error" ||
		item.reportsComputedAtRevision !== item.compositionRevision
	);
}

/** How long a queued compute may run before the lab stops waiting for it and says so. */
export const LAB_COMPUTE_STOP_MS = minutes(5);

/** How long a queued compute runs before the waiting wall adds that it is taking longer than usual. */
export const LAB_COMPUTE_SLOW_MS = seconds(60);

/** How long a queued compute keeps "Update Charts" off before the user may queue it again. */
export const LAB_COMPUTE_RETRY_MS = seconds(60);

/**
 * Whether the column's charts are waiting for a queued compute: one is out and the
 * {@link LAB_COMPUTE_STOP_MS} stop has not passed (see `PortfolioLabInvestmentItem.pendingCompute`).
 */
export function chartsComputing(item: Pick<PortfolioLabInvestmentItem, "pendingCompute">): boolean {
	return item.pendingCompute != null && !item.pendingCompute.timedOut;
}

/** Whether a pending compute is one SphereAI's recalculate started, which its call's result finishes (MDOT-25847). */
export function isBotCompute(pending: PendingCompute): pending is BotCompute {
	return "toolCallId" in pending;
}

/**
 * Whether the column's queued compute still keeps "Update Charts" off at `now`: a click would only
 * restart it, until it has waited {@link LAB_COMPUTE_RETRY_MS} with no result.
 */
export function computeHoldsUpdateCharts(
	item: Pick<PortfolioLabInvestmentItem, "pendingCompute">,
	now: number,
): boolean {
	const pending = item.pendingCompute;
	return pending != null && !pending.timedOut && now - pending.queuedAt < LAB_COMPUTE_RETRY_MS;
}

/** A compute's final outcome, which the `/compute` answer carries when nothing was queued, and the event otherwise. */
export type LabComputeOutcome = {
	status: Exclude<LabComputeStatusEnum, "QUEUED">;
	reportsComputedAtRevision?: number | null;
};

/** The final outcome in a `/compute` answer or a `lab-compute` event; `null` when the answer only says `QUEUED`. */
export function computeOutcome(
	answer: Pick<LabComputeEvent, "status" | "reportsComputedAtRevision">,
): LabComputeOutcome | null {
	const { status, reportsComputedAtRevision } = answer;
	return status === "QUEUED" ? null : { status, reportsComputedAtRevision };
}

/**
 * The column's `reportsComputedAtRevision` once a compute has finished. `OK` and `FALLBACK_USED`
 * stored reports at the revision they name; `FAILED` cleared them. `SUPERSEDED` stored nothing — the
 * revision it carries is the one the compute read — so the column keeps what it had.
 */
export function reportsRevisionAfterCompute(current: number | null, outcome: LabComputeOutcome): number | null {
	switch (outcome.status) {
		case "OK":
		case "FALLBACK_USED":
			return outcome.reportsComputedAtRevision ?? null;
		case "FAILED":
			return null;
		case "SUPERSEDED":
			return current;
		default:
			return unreachable(outcome.status);
	}
}

/**
 * The column's `scenarioFallback` once a compute has finished under `scenarioId`. `FALLBACK_USED`
 * records the scenario it fell back from; `OK` and `FAILED` replaced the numbers, so they clear it;
 * `SUPERSEDED` stored nothing, so the column keeps what it had.
 */
export function scenarioFallbackAfterCompute(
	current: PortfolioLabInvestmentItem["scenarioFallback"],
	outcome: LabComputeOutcome,
	scenarioId: string | null,
): PortfolioLabInvestmentItem["scenarioFallback"] {
	switch (outcome.status) {
		case "FALLBACK_USED":
			return scenarioId != null ? { scenarioId } : null;
		case "OK":
		case "FAILED":
			return null;
		case "SUPERSEDED":
			return current;
		default:
			return unreachable(outcome.status);
	}
}

/**
 * The column's `reportsScenarioId` once a compute has finished under `scenarioId`. `OK` stored reports
 * over that scenario's window; `FALLBACK_USED` over full history; `FAILED` cleared them; `SUPERSEDED`
 * stored nothing.
 */
export function reportsScenarioAfterCompute(
	current: string | null,
	outcome: LabComputeOutcome,
	scenarioId: string | null,
): string | null {
	switch (outcome.status) {
		case "OK":
			return scenarioId;
		case "FALLBACK_USED":
		case "FAILED":
			return null;
		case "SUPERSEDED":
			return current;
		default:
			return unreachable(outcome.status);
	}
}

/**
 * Whether the column's stored reports serve `scenarioId` without a compute. A historical scenario's own
 * line is stored apart, but Current Market's line and the metrics cover the window of the scenario the
 * last compute ran under, so they serve every scenario only after a compute over full history.
 */
export function reportsServeScenario(
	item: Pick<PortfolioLabInvestmentItem, "reportsScenarioId">,
	scenarioId: string | null,
): boolean {
	return item.reportsScenarioId == null || item.reportsScenarioId === scenarioId;
}

/**
 * Transient/in-progress statuses — the backend is still working on the investment and will emit an
 * `investment-update` SSE when it settles. Distinct from the "not updatable" set: `DRAFT`, `REVIEW`
 * and `ERROR` are also not updatable, but they are terminal (no further transition is coming), so
 * they never justify a watch/poll.
 */
const TRANSIENT_INVESTMENT_STATUSES: ReadonlySet<InvestmentStatus> = new Set<InvestmentStatus>([
	"CALCULATING",
	"RETRIEVING_DATA",
	"EXECUTING",
]);

/** Whether an investment's status is transient — still settling, a further SSE transition is expected. */
export function isTransientInvestmentStatus(status: InvestmentStatus): boolean {
	return TRANSIENT_INVESTMENT_STATUSES.has(status);
}

/**
 * Live gate for the "Update composition" menu action.
 *
 * For `INVESTMENT` / `INVESTMENT_PROPOSAL` (the entity types whose real status is kept live via the
 * SSE watch) the action is enabled only when the live status is updatable, no previous commit is
 * still settling (`commitSettleStatus === "waiting"`), and no commit request is in flight.
 * `BENCHMARK` / `REFERENCE` are not watched — they keep today's behavior (only the in-flight-save
 * disable; the click-time precondition check remains their status gate).
 */
export function canTriggerUpdateComposition(
	item: Pick<PortfolioLabInvestmentItem, "type" | "status" | "commitSettleStatus" | "compositionSaveStatus">,
): boolean {
	if (item.compositionSaveStatus === "saving") {
		return false;
	}
	if (item.type !== "INVESTMENT" && item.type !== "INVESTMENT_PROPOSAL") {
		return true;
	}
	return isUpdatableInvestmentStatus(item.status) && item.commitSettleStatus !== "waiting";
}

// #endregion

// #region Mathematical utilities

export function isNestedInvestment(item: PortfolioLabInvestmentItem): boolean {
	return item.currentComposition.some((c) => c.type === "portfolio");
}

/** Sum of the top-level effective weights — a nested portfolio counts once (its outer weight). Not rounded. */
function topLevelWeightTotal(composition: PortfolioLabComposition[]): number {
	return exactSum(composition, (c) => c.weight);
}

/**
 * Whether the top-level composition totals 100%: its unrounded total, rounded half-up to `precision`
 * (the customer's decimal places), equals 100. The totals colour, Solve Issue and the Update gate all read it.
 */
export function isCompositionExactly100(composition: PortfolioLabComposition[], precision: number): boolean {
	return compareAtPrecision(topLevelWeightTotal(composition), 100, precision) === 0;
}

/**
 * The weight totals cell: valid when {@link isCompositionExactly100}. The text stays at 2 decimals and an
 * invalid total rounds away from 100 (`99.996` → `99.99%`, `100.0000006` → `100.01%`), so it never reads
 * `100.00%` in the invalid colour. `total` is the unrounded sum, for the tooltip.
 */
export function labWeightTotalDisplay(
	composition: PortfolioLabComposition[],
	precision: number,
): { valid: boolean; text: string; total: number } {
	const total = topLevelWeightTotal(composition);
	const valid = isCompositionExactly100(composition, precision);
	return { valid, text: `${formatNumber(valid ? 100 : roundAwayFrom(total, 100, 2), 2)}%`, total };
}

// #endregion

// #region Update-composition commit helpers

/**
 * The entity-editor entities portfolio-lab can commit a composition to — a subset of the generated
 * `SaveEditorEditEntityEntityEnum` API contract (excludes `UNIVERSE` and `INVESTMENT_DRAFT`).
 * Derived via `Extract` so any rename/removal of these members in the API enum surfaces here as a
 * type error rather than silently drifting from the contract `instrumentsEditorRepository.edit` calls.
 */
export type LabEditorEntity = Extract<
	SaveEditorEditEntityEntityEnum,
	"INVESTMENT" | "INVESTMENT_ENHANCEMENT" | "BENCHMARK" | "TARGET_INVESTMENT"
>;

/** Maps a lab investment type to the entity-editor entity used to commit its composition to the real investment. */
export function editorEntityForLabType(type: PortfolioLabInvestmentType): LabEditorEntity {
	switch (type) {
		case "INVESTMENT":
			return "INVESTMENT";
		case "INVESTMENT_PROPOSAL":
			return "INVESTMENT_ENHANCEMENT";
		case "BENCHMARK":
			return "BENCHMARK";
		case "REFERENCE":
			return "TARGET_INVESTMENT";
	}
}

/**
 * Whether an "Update composition" commit should try to persist the lab's session AUM onto the real
 * investment: portfolios and proposals only (benchmarks/references stay composition-only), and only
 * when a session AUM is set and positive. This is the cheap, fetch-free gate — shared by the confirm
 * dialog's `willUpdateAum` announcement and the repository's commit path, so the two always agree.
 */
export function isAumCommitCandidate(item: Pick<PortfolioLabInvestmentItem, "type" | "aum">): boolean {
	if (item.type !== "INVESTMENT" && item.type !== "INVESTMENT_PROPOSAL") {
		return false;
	}
	return item.aum != null && item.aum > 0;
}

// #endregion

// #region Request cancellation

/**
 * Whether a rejection is "the request was cancelled" rather than a genuine failure.
 *
 * Axios rejects a request aborted through its `signal` with a `CanceledError` (`code:
 * "ERR_CANCELED"`); a `fetch`/`AbortSignal` rejection surfaces as a DOMException named
 * `"AbortError"`. Both mean the caller withdrew the request on purpose — the lab aborts a metrics
 * compute when its column is deleted (see `InvestmentComputes`) — so neither must be reported or
 * toasted as an error.
 */
export function isAbortError(error: unknown): boolean {
	if (error == null || typeof error !== "object") {
		return false;
	}
	const { code, name } = error as { code?: unknown; name?: unknown };
	return code === "ERR_CANCELED" || name === "CanceledError" || name === "AbortError";
}

// #endregion
